import React, { useMemo } from 'react';
import { Activity, ForEach, H5, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { Link } from 'react-router-dom';
import { PurchaseOrder } from '../../../Lib/Types/purchase-order';
import { Vendor } from '../../../Lib/Api/vendor';
import { vendorName } from '../../../Lib/Utils/Helpers/vendor-helper';
import { timeFromUuid } from '../Utils/timeFromUuid';
import { formatDate } from '../../PoCard/utils';

export type LegacyActivitiesProps = {
  purchaseOrder: PurchaseOrder;
  userId: string;
  vendor: Vendor;
};

const extractDateFromUuid = (uuid: string): Date | undefined => {
  try {
    return new Date(timeFromUuid(uuid));
  } catch (error) {
    return undefined;
  }
};

const getEvents = (purchaseOrder: PurchaseOrder, userId: string) => {
  const events: Array<{
    id: string;
    date: Date | undefined;
    update: React.ReactNode;
    isInternal: boolean;
  }> = [];

  if (purchaseOrder.created_date) {
    events.push({
      id: 'created_date',
      date: new Date(purchaseOrder.created_date),
      update: (
        <>
          created{' '}
          <span className="text-base-primary font-semibold">
            {purchaseOrder.po_number}
          </span>
        </>
      ),
      isInternal: true,
    });
  }

  if (purchaseOrder.date_of_acceptance) {
    events.push({
      id: 'date_of_acceptance',
      // This represents the date and not the time, so we can't show the relative time.
      // date: new Date(purchaseOrder.date_of_acceptance),
      date: undefined,
      update: (
        <>
          accepted{' '}
          <span className="text-base-primary font-semibold">
            {purchaseOrder.po_number}
          </span>
        </>
      ),
      isInternal: false,
    });
  }

  if (purchaseOrder.delivery?.booking?.starts_at) {
    const dateFromUuid = purchaseOrder.delivery?.booking?.id
      ? extractDateFromUuid(purchaseOrder.delivery.booking.id)
      : undefined;

    events.push({
      id: 'delivery',
      date: dateFromUuid,
      update: (
        <>
          scheduled a delivery for{' '}
          <span className="text-base-primary font-semibold">
            {formatDate(
              purchaseOrder.delivery.booking.starts_at,
              'delivery',
              'UTC',
            )}
          </span>{' '}
          to{' '}
          <span className="text-base-primary font-semibold">
            Fashion Nova DC
          </span>
        </>
      ),
      isInternal: true,
    });
  }

  if (purchaseOrder.received_date) {
    events.push({
      id: 'received_date',
      // This represents the date and not the time, so we can't show the relative time.
      // date: new Date(purchaseOrder.received_date),
      date: undefined,
      update: (
        <>
          received{' '}
          <span className="text-base-primary font-semibold">
            {purchaseOrder.po_number}
          </span>
        </>
      ),
      isInternal: true,
    });
  }

  purchaseOrder.bills?.forEach((bill) => {
    if (bill.ns_created_at && bill.invoice_number) {
      events.push({
        id: `ns_created_at-${bill.internal_id}`,
        date: new Date(bill.ns_created_at),
        update: (
          <>
            created invoice{' '}
            <Link to={`/users/${userId}/invoices/${bill.internal_id}`}>
              <span className="text-base-primary font-semibold">
                #{bill.invoice_number}
              </span>
            </Link>
          </>
        ),
        isInternal: true,
      });
    }
  });

  return events;
};

export function LegacyActivities(props: LegacyActivitiesProps) {
  const events = useMemo(
    () => getEvents(props.purchaseOrder, props.userId),
    [props.purchaseOrder, props.userId],
  );

  return (
    <Stack direction="col" className="gap-6">
      <H5>Activity</H5>
      <Stack direction="col" gap="sm" width="none">
        <ForEach items={events}>
          {(event, index) => {
            const name = event.isInternal
              ? 'Fashion Nova'
              : vendorName(props.vendor);
            const badge = event.isInternal ? undefined : 'vendor';
            const icon = event.isInternal ? 'ai/ai-02' : undefined;
            return (
              <Activity
                key={event.id}
                name={name}
                badgeType={badge}
                icon={icon}
                hideBar={index === events.length - 1}
              >
                <Activity.Event name={name} date={event.date}>
                  {event.update}
                </Activity.Event>
              </Activity>
            );
          }}
        </ForEach>
      </Stack>
    </Stack>
  );
}
